import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import {
  TrendingUp as SpendIcon,
  AccountBalanceWallet as BudgetIcon,
  Savings as SavingsIcon,
  Speed as UtilizationIcon,
  WarningAmber as RiskIcon,
} from '@mui/icons-material';
import { STORY_UI } from '../../solution-stories/storyUiTokens';
import { SEARCE_COLORS } from '../../../theme';
import type { BillingReportTotals } from '../../../utils/billingReportMetrics';
import { formatUsd } from '../../../utils/billingReportMetrics';

interface KpiTileDef {
  key: string;
  label: string;
  value: string;
  helper?: string;
  icon: React.ReactNode;
  accentColor: string;
  accentBg: string;
}

export interface BillingReportKpiCardsProps {
  totals: BillingReportTotals;
}

export function BillingReportKpiCards({ totals }: BillingReportKpiCardsProps): React.ReactElement {
  const tiles: KpiTileDef[] = [
    {
      key: 'budget',
      label: 'Total Annual Budget',
      value: formatUsd(totals.totalBudgetUsd),
      helper: `${totals.accountCount} accounts`,
      icon: <BudgetIcon sx={{ fontSize: 20 }} />,
      accentColor: STORY_UI.accent,
      accentBg: STORY_UI.accentTint,
    },
    {
      key: 'spend',
      label: 'Total YTD Spend',
      value: formatUsd(totals.totalSpendUsd),
      helper: `${totals.utilizationPct.toFixed(0)}% of budget`,
      icon: <SpendIcon sx={{ fontSize: 20 }} />,
      accentColor: SEARCE_COLORS.secondary,
      accentBg: '#EFEAFF',
    },
    {
      key: 'savings',
      label: 'Total Savings',
      value: formatUsd(totals.totalSavingsUsd),
      helper: 'Budget − spend, completed months',
      icon: <SavingsIcon sx={{ fontSize: 20 }} />,
      accentColor: SEARCE_COLORS.successDark,
      accentBg: '#E4F9EE',
    },
    {
      key: 'utilization',
      label: 'Budget Utilization',
      value: `${totals.utilizationPct.toFixed(1)}%`,
      icon: <UtilizationIcon sx={{ fontSize: 20 }} />,
      accentColor: STORY_UI.navy2,
      accentBg: STORY_UI.accentTint,
    },
    {
      key: 'atRisk',
      label: 'At-Risk Accounts',
      value: String(totals.atRiskCount),
      helper: totals.totalOverspendUsd > 0 ? `${formatUsd(totals.totalOverspendUsd)} overspent` : 'None overspent',
      icon: <RiskIcon sx={{ fontSize: 20 }} />,
      accentColor: totals.atRiskCount > 0 ? SEARCE_COLORS.errorDark : SEARCE_COLORS.successDark,
      accentBg: totals.atRiskCount > 0 ? '#FDE9EB' : '#E4F9EE',
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
        gap: 2,
      }}
    >
      {tiles.map((t) => (
        <Box
          key={t.key}
          sx={{
            backgroundColor: STORY_UI.surface,
            border: `1px solid ${STORY_UI.line}`,
            borderRadius: STORY_UI.radiusLg,
            boxShadow: STORY_UI.shadowCard,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            minWidth: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              sx={{
                fontFamily: STORY_UI.fontSans,
                fontSize: '12px',
                fontWeight: 600,
                color: STORY_UI.inkFaint,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
              }}
            >
              {t.label}
            </Typography>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: STORY_UI.radiusSm,
                backgroundColor: t.accentBg,
                color: t.accentColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {t.icon}
            </Box>
          </Box>
          <Typography
            sx={{
              fontFamily: STORY_UI.fontDisplay,
              fontWeight: 700,
              fontSize: '26px',
              color: STORY_UI.ink,
              lineHeight: 1.15,
            }}
          >
            {t.value}
          </Typography>
          {t.key === 'utilization' ? (
            <LinearProgress
              variant="determinate"
              value={Math.min(100, totals.utilizationPct)}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: STORY_UI.accentTint,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  backgroundColor:
                    totals.utilizationPct >= 100 ? SEARCE_COLORS.errorDark : STORY_UI.accent,
                },
              }}
            />
          ) : (
            t.helper && (
              <Typography sx={{ fontFamily: STORY_UI.fontSans, fontSize: '12px', color: STORY_UI.inkFaint }}>
                {t.helper}
              </Typography>
            )
          )}
        </Box>
      ))}
    </Box>
  );
}
